import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2,
  Warehouse,
  Factory,
  Truck,
  Briefcase,
  Clock,
  Users,
  Shield
} from 'lucide-react';

export default function IndustriesPage() {
  const industries = [
    {
      id: 'warehousing',
      title: 'Warehousing & Distribution',
      description: 'Reliable workforce solutions for distribution centers, fulfillment operations, and warehouse facilities across the GTA.',
      icon: Warehouse,
      image: 'https://images.pexels.com/photos/4487382/pexels-photo-4487382.jpeg',
      roles: [
        'Forklift Operators (Counterbalance, Reach, Walkie)',
        'Warehouse Associates',
        'Order Pickers & Packers',
        'Shipping & Receiving Clerks',
        'Inventory Specialists',
        'Material Handlers',
        'RF Scanner Operators',
        'Dock Workers'
      ],
      benefits: [
        'Pre-certified forklift operators ready to deploy',
        'Workers experienced with WMS systems',
        'Flexible shift coverage (day, afternoon, night)',
        'Scalable teams for peak seasons'
      ],
      speed: '24-48 hours'
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      description: 'Skilled production workers and machine operators for light and heavy manufacturing operations.',
      icon: Factory,
      image: 'https://images.unsplash.com/photo-1768796373634-db43bfd5f064?crop=entropy&cs=srgb&fm=jpg',
      roles: [
        'Machine Operators',
        'Assembly Line Workers',
        'Quality Control Inspectors',
        'CNC Operators',
        'Production Supervisors',
        'General Labourers',
        'Packaging Specialists',
        'Maintenance Helpers'
      ],
      benefits: [
        'Workers trained in safety protocols',
        'Experience with lean manufacturing',
        'Quality-focused candidates',
        'Temp-to-perm options available'
      ],
      speed: '48-72 hours'
    },
    {
      id: 'transportation',
      title: 'Transportation & Logistics',
      description: 'Drivers, dispatchers, and logistics professionals to keep your supply chain moving.',
      icon: Truck,
      image: 'https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg',
      roles: [
        'Class AZ/DZ Drivers',
        'Delivery Drivers (G License)',
        'Dispatchers',
        'Route Planners',
        'Logistics Coordinators',
        'Yard Jockeys',
        'Load Planners',
        'Transportation Clerks'
      ],
      benefits: [
        'Pre-screened driving records',
        'Licensed and certified drivers',
        'Knowledge of GTA routes',
        'Available for local and long-haul'
      ],
      speed: '48-72 hours'
    },
    {
      id: 'office',
      title: 'Office & Administrative',
      description: 'Professional administrative support staff for reception, data entry, and office management.',
      icon: Briefcase,
      image: 'https://images.unsplash.com/photo-1642522029691-029b5a432954?crop=entropy&cs=srgb&fm=jpg',
      roles: [
        'Administrative Assistants',
        'Receptionists',
        'Data Entry Clerks',
        'Customer Service Representatives',
        'Office Managers',
        'Executive Assistants',
        'HR Coordinators',
        'Accounting Clerks'
      ],
      benefits: [
        'Proficient in MS Office & Google Suite',
        'Professional communication skills',
        'Background-verified candidates',
        'Bilingual options available'
      ],
      speed: '24-48 hours'
    }
  ];

  return (
    <main data-testid="industries-page">
      {/* Hero Section */}
      <section className="page-header" data-testid="industries-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
              Industries We Serve
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Specialized Staffing Across Industries
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              Deep expertise in key sectors means faster placements and better-matched candidates for your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry Quick Links */}
      <section className="py-8 bg-white border-b border-[#E2E8F0] sticky top-20 z-40" data-testid="industry-quick-links">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <a
                key={industry.id}
                href={`#${industry.id}`}
                className="category-pill flex items-center gap-2"
              >
                <industry.icon size={16} strokeWidth={1.5} />
                {industry.title.split(' & ')[0]}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Sections */}
      {industries.map((industry, index) => (
        <section 
          key={industry.id}
          id={industry.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-[#F5F7FA]'}`}
          data-testid={`industry-${industry.id}`}
        >
          <div className="container-custom">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#0B1F3A] rounded-lg flex items-center justify-center">
                    <industry.icon size={24} className="text-white" strokeWidth={1.5} />
                  </div>
                  <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                    {industry.title.split('&')[0].trim()}
                  </span>
                </div>
                
                <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] tracking-tight mb-4">
                  {industry.title}
                </h2>
                
                <p className="text-[#64748B] text-lg leading-relaxed mb-8">
                  {industry.description}
                </p>

                {/* Speed Badge */}
                <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full mb-8">
                  <Clock size={16} strokeWidth={1.5} />
                  <span className="text-sm font-bold">Deployment Time: {industry.speed}</span>
                </div>

                {/* Benefits */}
                <div className="space-y-3 mb-8">
                  {industry.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="text-[#1E90FF] flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-[#0B1F3A]">{benefit}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/employers#request-form"
                  className="btn-primary btn-hover-arrow"
                  data-testid={`cta-${industry.id}`}
                >
                  Request Staff
                  <ArrowRight size={16} className="arrow-icon" strokeWidth={2} />
                </Link>
              </motion.div>

              {/* Roles Card */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <div className="relative">
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 rounded-lg opacity-10"
                    style={{
                      backgroundImage: `url(${industry.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  
                  <div className="relative bg-white border border-[#E2E8F0] p-8">
                    <h3 className="font-['Chivo'] font-bold text-xl text-[#0B1F3A] mb-6 flex items-center gap-2">
                      <Users size={20} className="text-[#1E90FF]" strokeWidth={1.5} />
                      Roles We Fill
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {industry.roles.map((role) => (
                        <div 
                          key={role}
                          className="flex items-center gap-2 text-sm text-[#0B1F3A] bg-[#F5F7FA] px-3 py-2 rounded"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
                          {role}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="industries-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
              Don't See Your Industry?
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-8">
              We work with businesses across many sectors. Contact us to discuss your specific staffing needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary btn-hover-arrow text-lg px-8 py-4"
                data-testid="contact-cta"
              >
                Contact Us
                <ArrowRight size={20} className="arrow-icon" strokeWidth={2} />
              </Link>
              <a
                href="mailto:info@fleethire.ca"
                className="btn-outline border-white text-white hover:bg-white hover:text-[#0B1F3A] text-lg px-8 py-4"
              >
                Email: info@fleethire.ca
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
